import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
    TrendingUp, 
    Users, 
    Calendar, 
    CreditCard, 
    Download,
    RefreshCw,
    Loader2,
    PoundSterling,
    BarChart3,
    PieChart,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2E7D32', '#4CAF50', '#81C784', '#A5D6A7', '#C8E6C9'];

const AnalyticsDashboard = ({ token }) => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [timeRange, setTimeRange] = useState('month');

    const authAxios = axios.create({
        baseURL: API,
        headers: { 'Authorization': `Bearer ${token}` }
    });

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const response = await authAxios.get(`/admin/analytics/dashboard?time_range=${timeRange}`);
            setAnalytics(response.data);
        } catch (error) {
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange]);

    const exportData = async (dataType) => {
        try {
            const response = await authAxios.get(`/admin/analytics/export?data_type=${dataType}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${dataType}_export.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Export downloaded');
        } catch (error) {
            toast.error('Failed to export data');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const { revenue, bookings, customers, payments } = analytics || {};

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold font-serif">Analytics Dashboard</h2>
                    <p className="text-muted-foreground">Business intelligence & insights</p>
                </div>
                <div className="flex gap-2">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="px-3 py-2 border rounded-lg text-sm"
                    >
                        <option value="week">Last 7 Days</option>
                        <option value="month">Last 30 Days</option>
                        <option value="quarter">Last 3 Months</option>
                        <option value="year">Last Year</option>
                        <option value="all_time">All Time</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={fetchAnalytics}>
                        <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportData('bookings')}>
                        <Download className="w-4 h-4 mr-1" /> Export
                    </Button>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Revenue</p>
                                <p className="text-3xl font-bold">£{(revenue?.total_revenue || 0).toFixed(2)}</p>
                                <p className="text-xs text-green-600 flex items-center mt-1">
                                    <ArrowUpRight className="w-3 h-3" /> Net: £{(revenue?.net_revenue || 0).toFixed(2)}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <PoundSterling className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Bookings</p>
                                <p className="text-3xl font-bold">{bookings?.total_bookings || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {bookings?.confirmed_bookings || 0} confirmed
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Customers</p>
                                <p className="text-3xl font-bold">{customers?.total_customers || 0}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {customers?.returning_customers || 0} returning
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Payment Success</p>
                                <p className="text-3xl font-bold">{(payments?.success_rate || 0).toFixed(1)}%</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {payments?.successful_payments || 0} successful
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                                <CreditCard className="w-6 h-6 text-amber-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5" /> Revenue Trend
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={revenue?.revenue_by_month || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip formatter={(value) => `£${value}`} />
                                <Bar dataKey="revenue" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Bookings by Session */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <PieChart className="w-5 h-5" /> Bookings by Session
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                            <RechartsPieChart>
                                <Pie
                                    data={bookings?.bookings_by_session || []}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ session_name, bookings }) => `${session_name}: ${bookings}`}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="bookings"
                                    nameKey="session_name"
                                >
                                    {(bookings?.bookings_by_session || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </RechartsPieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Revenue Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Revenue Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Booking Revenue</span>
                            <span className="font-semibold">£{(revenue?.booking_revenue || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Voucher Revenue</span>
                            <span className="font-semibold">£{(revenue?.voucher_revenue || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Stripe Fees (Est.)</span>
                            <span className="font-semibold text-red-600">-£{(revenue?.stripe_fees || 0).toFixed(2)}</span>
                        </div>
                        <div className="border-t pt-4 flex justify-between items-center">
                            <span className="font-semibold">Net Revenue</span>
                            <span className="font-bold text-lg text-green-600">£{(revenue?.net_revenue || 0).toFixed(2)}</span>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Booking Stats</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Occupancy Rate</span>
                            <Badge variant={bookings?.occupancy_rate > 50 ? "default" : "secondary"}>
                                {(bookings?.occupancy_rate || 0).toFixed(1)}%
                            </Badge>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Avg. Booking Value</span>
                            <span className="font-semibold">£{(revenue?.average_booking_value || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Waitlist Entries</span>
                            <span className="font-semibold">{bookings?.total_waitlist || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Pending Bookings</span>
                            <Badge variant="outline">{bookings?.pending_bookings || 0}</Badge>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Top Customers</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {(customers?.top_customers || []).slice(0, 5).map((customer, idx) => (
                                <div key={customer.email || `customer-${idx}`} className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold">
                                            {idx + 1}
                                        </div>
                                        <span className="text-sm truncate max-w-[120px]">{customer.email}</span>
                                    </div>
                                    <span className="font-semibold text-sm">£{customer.total_spent?.toFixed(2)}</span>
                                </div>
                            ))}
                            {(!customers?.top_customers || customers.top_customers.length === 0) && (
                                <p className="text-muted-foreground text-sm text-center py-4">No customer data yet</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
